// The Alarms page "Notifications" section (Phase UI-5, extended by ADR 0171):
// a per-user, per-boat email-notification preference card. Self-scoped (each
// user their own row), backed by useNotificationPrefs (§5.9 / ADR 0153 + 0171).
//
// Four settings, in the order an owner thinks about them: do you want alarm
// mail at all → how loud does it have to be → tell me when it goes back to
// normal → tell me if the boat itself stops talking.
//
// Honesty invariants:
//   - NO optimistic UI: every control always reflects the value the PUT
//     RETURNED (useNotificationPrefs reflects the response), so a rejected write
//     can't leave a control lying about what the server stored.
//   - When delivery is off in this environment (dry run — the prod gate and/or
//     the NEOMARIS_CLOUD_ALERT_EMAILS kill switch), a clearly-visible, non-alarming
//     note says so plainly rather than presenting a toggle that silently sends
//     nothing. Alarm activity is still recorded either way.
//   - The severity floor renders FROM the shared severity vocabulary
//     (ALARM_SEVERITIES) with inclusive "and above" labels.
//   - The ADR 0153 trailing-hour throttle used to suppress SILENTLY. When the
//     server reports it active, the card SAYS so — and says whose limit it is:
//     the cap keys on (boat, alarm) and is shared by every owner, so the banner
//     must not read as a personal quota. There is no reset control because the
//     backend ships none: the rows the throttle counts are also the delivery
//     dedupe ledger, and clearing the window would re-send the same alarm
//     occurrences to the same people (ADR 0171 §C). It is suppressed for a
//     reader whose own master switch is off — "paused" would be false for
//     someone who was never going to be mailed (`showRateLimitBanner`).

import { useDemoGuest } from '../../auth';
import { ALARM_SEVERITIES, type AlarmSeverity } from '../../lib/alarmDisplay';
import {
  minSeverityFloorLabel,
  offlineThresholdLabel,
  offlineThresholdOptions,
  rateLimitDetail,
  showRateLimitBanner,
  useNotificationPrefs,
} from '../../lib/useNotificationPrefs';

const EMAIL_LABEL = "Email me about this boat's alarms — sent to your account email";
// "goes back to normal", not "clears": `Normal` is the state the boat reports
// and the word the recovery email's own subject uses, while "cleared" was
// retired from this vocabulary for reading like the alarm is over and gone when
// it names an acknowledgement.
const RECOVERY_LABEL =
  'Email me when an alarm goes back to normal — the all-clear, not just the bad news';
const OFFLINE_LABEL = 'Email me if this boat goes quiet — and again when it comes back';
const DRY_RUN_NOTE =
  'Email delivery is disabled in this environment (dry run) — alarm activity is still recorded.';
const RATE_LIMITED_NOTE =
  'Notifications paused — too many in the last hour. Resumes automatically.';

export function NotificationPrefsCard({ boatId }: { boatId: string }) {
  // A Try it guest gets no alarm notices and cannot change who does (TOM-868).
  // The hook reads nothing for an empty boat id, so a guest's card reads nothing.
  const guest = useDemoGuest();
  const { state, saving, saveOutcome, saveError, save, reload } = useNotificationPrefs(
    guest ? '' : boatId
  );
  if (guest) return null;

  return (
    <section className="alarm-band alarm-notify" data-testid="alarms-notification-prefs">
      <h2 className="alarm-band__title">Notifications</h2>

      {state.kind === 'loading' ? (
        <p className="muted" data-testid="notify-loading">
          Loading your notification settings…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="empty-card" data-testid="notify-error">
          <p className="muted">{state.message}</p>
          <button type="button" className="btn btn-outline" onClick={reload}>
            Try again
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <div className="alarm-notify__body">
          <label className="alarm-notify__check">
            <input
              type="checkbox"
              checked={state.prefs.emailEnabled}
              disabled={saving}
              onChange={(e) => save({ emailEnabled: e.target.checked })}
              data-testid="notify-email-toggle"
            />
            <span>{EMAIL_LABEL}</span>
          </label>

          <div className="alarm-notify__field">
            <label htmlFor="notify-severity">Notify me about</label>
            <select
              id="notify-severity"
              className="sort-select alarm-notify__select"
              value={state.prefs.minSeverity}
              disabled={saving}
              onChange={(e) => save({ minSeverity: e.target.value as AlarmSeverity })}
              data-testid="notify-severity-select"
            >
              {ALARM_SEVERITIES.map((sev) => (
                <option key={sev} value={sev}>
                  {minSeverityFloorLabel(sev)}
                </option>
              ))}
            </select>
          </div>

          <label className="alarm-notify__check">
            <input
              type="checkbox"
              checked={state.prefs.recoveryEmailEnabled}
              disabled={saving}
              onChange={(e) => save({ recoveryEmailEnabled: e.target.checked })}
              data-testid="notify-recovery-toggle"
            />
            <span>{RECOVERY_LABEL}</span>
          </label>

          <label className="alarm-notify__check">
            <input
              type="checkbox"
              checked={state.prefs.offlineAlertEnabled}
              disabled={saving}
              onChange={(e) => save({ offlineAlertEnabled: e.target.checked })}
              data-testid="notify-offline-toggle"
            />
            <span>{OFFLINE_LABEL}</span>
          </label>

          <div
            className={`alarm-notify__field${
              state.prefs.offlineAlertEnabled ? '' : ' alarm-notify__field--inert'
            }`}
          >
            <label htmlFor="notify-offline-threshold">Tell me after</label>
            <select
              id="notify-offline-threshold"
              className="sort-select alarm-notify__select"
              value={String(state.prefs.offlineThresholdMinutes)}
              // The threshold only means anything once the alert is ON, so it
              // stays inert until then rather than inviting a setting that does
              // nothing.
              disabled={saving || !state.prefs.offlineAlertEnabled}
              onChange={(e) => save({ offlineThresholdMinutes: Number(e.target.value) })}
              data-testid="notify-offline-threshold"
            >
              {offlineThresholdOptions(state.prefs.offlineThresholdMinutes).map((minutes) => (
                <option key={minutes} value={minutes}>
                  {offlineThresholdLabel(minutes)}
                </option>
              ))}
            </select>
          </div>

          <span className="alarm-notify__status" aria-live="polite">
            {saving ? (
              <span className="alarm-notify__saving" data-testid="notify-saving">
                Saving…
              </span>
            ) : saveOutcome === 'saved' ? (
              <span className="alarm-notify__saved" data-testid="notify-saved">
                Saved
              </span>
            ) : saveOutcome === 'error' ? (
              <span className="alarm-notify__err" role="alert" data-testid="notify-save-err">
                {saveError ?? 'Couldn’t save — try again.'}
              </span>
            ) : null}
          </span>

          {showRateLimitBanner(state.prefs) ? (
            <div className="alarm-notify__paused" role="status" data-testid="notify-rate-limited">
              <p className="alarm-notify__paused-head">{RATE_LIMITED_NOTE}</p>
              <p className="alarm-notify__paused-detail" data-testid="notify-rate-limited-detail">
                {rateLimitDetail(state.prefs.rateLimit)}
              </p>
            </div>
          ) : null}

          {!state.prefs.deliveryEnabled ? (
            <p className="alarm-notify__dry-run" data-testid="notify-dry-run-note">
              {DRY_RUN_NOTE}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
